import { Box, Center, Spinner } from '@chakra-ui/react';
import {
  DeleteOutlineIcon,
  DuplicateOutlineIcon,
  EditOutlineIcon,
} from '@diamond/shared/icons';
import { ProductHighlightAdminCg3 } from '@diamond/shared/types';
import {
  ActionButton,
  DropResult,
  PageLoader,
  Sortable,
  SortableItem,
} from '@diamond/shared/ui';
import { adminCaptureException } from '@diamond/shared/utils';
import { LIST_FEATURE_FLAGS, useFeatureFlag } from '@diamond/shared/utils';
import { AdminLayout } from '@diamond/sol-admin/components';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

import { HighlightItem } from '../components/highlight-item';
import {
  type HighlightModalHandlers,
  HighlightModalContainer,
} from '../components/highlight-modal-container';
import { HighlightPageHeader } from '../components/highlight-page-header';
import {
  BACKOFFICE_HIGHLIGHT_QUERY_KEY,
  HIGHLIGHT_PAGE_SIZE,
} from '../constant';
import { useProductHighlights, useReorderProductHighlight } from '../hooks';
import { useHighlightsListInfinite } from '../hooks/infinite';

export function HighlightPage() {
  const modalRef = useRef<HighlightModalHandlers>(null);
  const { ref, inView } = useInView();
  const queryClient = useQueryClient();
  const links = [
    { title: 'Dashboard', link: '/' },
    { title: 'Highlight Product', link: '/highlight' },
  ];

  // Feature flag: new CG3 paginated endpoint with infinite scroll
  const isNewCg3 = useFeatureFlag(
    LIST_FEATURE_FLAGS.FF_HIGHLIGHT_CG3_NEW,
    false
  );

  const legacyList = useProductHighlights();

  const cg3List = useHighlightsListInfinite(
    { size: HIGHLIGHT_PAGE_SIZE, page: 1 },
    { enabled: isNewCg3 }
  );

  const cg3Flattened: ProductHighlightAdminCg3['data'] = useMemo(
    () =>
      (cg3List.data?.pages.flatMap((p) => p.data) ??
        []) as ProductHighlightAdminCg3['data'],
    [cg3List.data]
  );

  useEffect(() => {
    if (
      isNewCg3 &&
      inView &&
      cg3List.hasNextPage &&
      !cg3List.isFetchingNextPage
    ) {
      cg3List.fetchNextPage();
    }
  }, [isNewCg3, inView, cg3List.hasNextPage, cg3List.isFetchingNextPage]);

  const allProductHighlightData = useMemo(() => {
    const data = isNewCg3
      ? cg3List.data?.pages.flatMap((p) => p.data) ?? []
      : legacyList.data ?? [];
    return data.map(
      ({
        id,
        title,
        product_highlight_type,
        total_business_types,
        total_shiptos,
        distribution_channel,
        business_segmentation,
      }) => ({
        id,
        title,
        product_highlight_type,
        total_business_types,
        total_shiptos,
        distribution_channel,
        business_segmentation,
      })
    );
  }, [isNewCg3, legacyList.data, cg3List.data]);

  const reorderHighlightHook = useReorderProductHighlight();

  const handleDuplicate = useCallback((targetId: string) => {
    modalRef.current?.handleOpenDuplicate(targetId);
  }, []);

  const handleEditAction = useCallback((id: string) => {
    modalRef.current?.handleOpenEdit(id);
  }, []);

  const handleDeleteAction = useCallback((id: string) => {
    modalRef.current?.handleOpenDelete(id);
  }, []);

  const highlights: SortableItem[] = useMemo(() => {
    if (isNewCg3) {
      return cg3Flattened.map((h) => ({
        id: h.id,
        originalIndex: h.index,
        children: (
          <HighlightItem
            key={h.id}
            data={{
              id: h.id,
              title: h.title,
              product_highlight_type: h.product_highlight_type,
              total_business_types: h.total_business_types,
              total_shiptos: h.total_shiptos,
              distribution_channel: h.distribution_channel,
              business_segmentation: h.business_segmentation,
            }}
            ActionButton={() => (
              <ActionButton
                editIcon={<EditOutlineIcon />}
                deleteIcon={<DeleteOutlineIcon />}
                duplicateIcon={<DuplicateOutlineIcon />}
                onClickDuplicate={() => handleDuplicate(h.id)}
                onClickEdit={() => handleEditAction(h.id)}
                onClickDelete={() => handleDeleteAction(h.id)}
              />
            )}
          />
        ),
      }));
    }

    return allProductHighlightData.map((h) => ({
      id: h.id,
      children: (
        <HighlightItem
          key={h.id}
          data={h}
          ActionButton={() => (
            <ActionButton
              editIcon={<EditOutlineIcon />}
              deleteIcon={<DeleteOutlineIcon />}
              duplicateIcon={<DuplicateOutlineIcon />}
              onClickDuplicate={() => handleDuplicate(h.id)}
              onClickEdit={() => handleEditAction(h.id)}
              onClickDelete={() => handleDeleteAction(h.id)}
            />
          )}
        />
      ),
    }));
  }, [
    isNewCg3,
    cg3Flattened,
    allProductHighlightData,
    handleEditAction,
    handleDeleteAction,
    handleDuplicate,
  ]);

  const updateIndex = useCallback(
    async (targetId: string, index: number) => {
      try {
        const result = await reorderHighlightHook.mutateAsync({
          id: targetId,
          index,
        });
        if (isNewCg3) {
          // Invalidate CG3 list to refresh pages after reorder
          queryClient.invalidateQueries({
            queryKey: [BACKOFFICE_HIGHLIGHT_QUERY_KEY],
          });
        }
        return result;
      } catch (error: unknown) {
        if (isAxiosError(error)) {
          const axiosError = error;
          adminCaptureException(axiosError.response?.data.message, (scope) => {
            scope.setTags({
              feature: 'highlight',
              action: 'reorderHighlight',
              status_code: axiosError.response?.data.statusCode,
            });
            scope.setContext('error', {
              error: axiosError.response?.data,
            });
            scope.setExtra('reorderHighlight', {
              id: targetId,
              index,
            });
            scope.setLevel('error');
          });
        }
        if (error instanceof Error) throw error;
        throw new Error(String(error));
      }
    },
    [reorderHighlightHook, isNewCg3, queryClient]
  );

  const handleIndexChange = useCallback(
    (result: DropResult, items: SortableItem[]) => {
      if (!result.destination) return;
      if (items && items.length) {
        const destinationIdx = (items[result.destination.index] as any)
          .originalIndex as number | undefined;
        if (typeof destinationIdx === 'number') {
          updateIndex(result.draggableId, destinationIdx);
          return;
        }
      }
      // Fallback to position-based index (legacy behavior)
      updateIndex(result.draggableId, result.destination.index + 1);
    },
    [updateIndex]
  );

  const isLoading = isNewCg3 ? cg3List.isPending : legacyList.isLoading;
  if (isLoading) {
    return (
      <AdminLayout>
        <HighlightPageHeader title="Highlight Product" breadcrumbs={links} />
        <PageLoader />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <HighlightPageHeader title="Highlight Product" breadcrumbs={links} />
      <HighlightModalContainer
        ref={modalRef}
        allProductHighlightData={allProductHighlightData}
        refetch={isNewCg3 ? cg3List.refetch : legacyList.refetch}
      />
      <Box my={8}>
        <Sortable data={highlights} handleChange={handleIndexChange} />
        {isNewCg3 && (
          <>
            <Box ref={ref} />
            {cg3List.isFetchingNextPage && (
              <Center mt={4}>
                <Spinner />
              </Center>
            )}
          </>
        )}
      </Box>
    </AdminLayout>
  );
}
